import * as React from 'react'

import './TanStackTable.scss' ;

function DebouncedInput({
                            value: initialValue,
                            onChange,
                            debounce = 500,
                            ...props
                        }: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = React.useState(initialValue)

    React.useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value])

    return (
        <input {...props} value={value} onChange={e => setValue(e.target.value)} />
    )
}
import {
    Column,
    ColumnDef,
    PaginationState,
    Table,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable, FilterFn, RowData,
} from '@tanstack/react-table'
import TanStackColumnHeader from "./TanStackColumnHeader";

import {
    RankingInfo,
    rankItem,
    compareItems,
} from '@tanstack/match-sorter-utils'

declare module '@tanstack/react-table' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>
    }
    interface FilterMeta {
        itemRank: RankingInfo
    }
    interface ColumnMeta<TData extends RowData, TValue> {
        textAlign?: 'left' | 'center' | 'right' ;
    }
}


const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    // Rank the item
    const itemRank = rankItem(row.getValue(columnId), value)

    // Store the itemRank info
    addMeta({
        itemRank,
    })

    // Return if the item should be filtered in/out
    return itemRank.passed
}

interface props {
    data: [any],
    columns: [any],
    showSearch: boolean
}

const TanStackTable: React.FC<props> = ({data, columns, showSearch}) => {
    const [pagination, setPagination] = React.useState<PaginationState>({
        pageIndex: 0,
        pageSize: 12,
    })

    const [globalFilter, setGlobalFilter] = React.useState('')

    const table = useReactTable({
        data,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        debugTable: false,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onGlobalFilterChange: setGlobalFilter,
        /* globalFilterFn: fuzzyFilter,  NS REMOVED THIS 14/09/224 as it seems to prevent search working as expected */
        onPaginationChange: setPagination,
        //no need to pass pageCount or rowCount with client-side pagination as it is calculated automatically
        state: {
            pagination,
            globalFilter,
        },
    })

    const tablePaginator = () => {
        if (!table.getCanNextPage() && !table.getCanPreviousPage()) {
            return <div/>
        } else {
            return (
                <div className="pagination">
                    <div className='button-container'>
                        <button onClick={() => table.lastPage()} disabled={!table.getCanNextPage()}>{'>>'}</button>
                        {' '}
                        <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>{'>'}</button>
                        {' '}
                        <button onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}>{'<'}</button>
                        {' '}
                        <button onClick={() => table.firstPage()} disabled={!table.getCanPreviousPage()}>{'<<'}</button>
                    </div>
                    <div className='pagination-container'>
                        <span className='page-div'>Page{' '}
                            <strong>{table.getState().pagination.pageIndex + 1} of {table.getPageCount().toLocaleString()}</strong>{' '}
                    </span>
                        <span>
          | Go to page:{' '}
                            <input
                                type="number"
                                defaultValue={table.getState().pagination.pageIndex + 1}
                                onChange={e => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0
                                    table.setPageIndex(page)
                                }}
                                style={{width: '100px'}}
                            />
                        </span>{' '}
                        <select className='select-div'
                                value={table.getState().pagination.pageSize}
                                onChange={e => {
                                    table.setPageSize(Number(e.target.value))
                                }}
                        >
                            {[12, 24, 36, 48, 60].map(pageSize => (
                                <option key={pageSize} value={pageSize}>
                                    Show {pageSize}
                                </option>
                            ))}
                        </select>

                    </div>
                </div>
            )

        }
    }

    return (
        <div className="tanstack-table">
            {showSearch === true &&
                <div className='table-search'>
                    <div/>
                    <DebouncedInput
                        value={globalFilter ?? ''}
                        onChange={value => setGlobalFilter(String(value))}
                        className="p-2 font-lg border border-block"
                        placeholder=" Search all columns..."
                    />
                </div>}
            <table className='table table-sm table-striped table-bordered table-hover'>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => {
                            return (
                                <th key={header.id} colSpan={header.colSpan} style={header.column.columnDef.meta}>
                                    <TanStackColumnHeader header={header} />
                                </th>
                            )
                        })}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => {
                    return (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {

                                const align = cell.column.columnDef?.meta?.textAlign !== undefined ? { textAlign: cell.column.columnDef.meta.textAlign } : {} ;

                                return (
                                    <td key={cell.id} style={align}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                )
                            })}
                        </tr>
                    )
                })}
                </tbody>
            </table>
            {tablePaginator()}
        </div>
    )
}

export default TanStackTable;

